import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController } from '@ionic/angular';

import { get } from '@providers/common/app-storage.service';
import { ApiProvider } from '@providers/common/api.service';
import { apiURL, apiEndpoints } from '@common/constants';
import { apiCivarLogin } from '@common/constants';
import { UtilityFunctionService } from '@providers/common/utilityFunction.service';

import { appVersionInfo } from '@interfaces/version.interface';

import { SecureStorage } from '@aparajita/capacitor-secure-storage';

interface UserCredentials {
    username: string;
    password: string;
}

@Injectable({
    providedIn: 'root'
})
export class LoginProvider {
    private api = inject(ApiProvider);
    private alertCtrl = inject(AlertController);
    private utilityServ = inject(UtilityFunctionService);


    token = '';

    /**
     * @name login
     * @desc Método para mandar credenciales a API y validarlas.
     * @param {Object} credentials viene la info de inicio de sesión
     * @returns {Object} - Promise
     */
    login(credentials: any, _versionApp: string, _platformApp: string) {
        const records = {
            Login: {
                username: credentials.username,
                password: credentials.password,
                // appVersion: versionApp,
                // appPlatform: platformApp
            }
        };

        // console.log('Body info to service: ', JSON.stringify(records));

        return new Promise((resolve, reject) => {
            const endpoint = apiEndpoints.login;
            this.api.post(endpoint, records).subscribe({
                next: (data: any) => {
                    // console.log('Response data service: ', JSON.stringify(data));

                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve(data.Token);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }

    saveToken(token: string) {
        const records = {
            NotificationToken: token
        };
        return new Promise((resolve, reject) => {
            const endpoint = apiEndpoints.NotificationRegister;
            this.api.post(endpoint, records, apiURL).subscribe({
                next: (data: any) => {
                    // console.log('saveToken: ', JSON.stringify(data), ' - token: ', records);

                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve(data.Token);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }

    /**
     * @name logout
     * @desc Método que hace un request post para cerrar la sesion del usuario
     * @returns {Object} Observable
     */
    logout() {
        const endpoint = apiEndpoints.logout;
        return this.api.post(endpoint);
    }

    /**
     * @name promptLogout
     * @desc Método que muestra un mensaje de confirmación cuando se presiona el boton de Salir del menu
     */
    async promptLogout() {
        // La promesa se resuelve desde los botones del alert; crear el alert fuera
        // del executor evita que un error al crearlo deje la promesa sin resolver
        let resolve!: (value: boolean) => void;
        const result = new Promise<boolean>((r) => (resolve = r));
        const alert = await this.alertCtrl.create({
            header: 'Salir',
            message: '¿Está seguro de querer cerrar sesión?',
            buttons: [
                {
                    text: 'Cancelar',
                    role: 'cancel',
                    handler: () => {
                        resolve(false);
                    }
                },
                {
                    text: 'Cerrar sesión',
                    handler: () => {
                        this.logout().subscribe({
                            next: () => {
                                resolve(true);
                            },
                            error: () => {
                                resolve(true);
                            },
                        });
                    }
                }
            ],
            cssClass: 'base-alert'
        });

        await alert.present();
        return result;
    }

    loginCivar(token: any) {
        return new Promise<void>((resolve, reject) => {
            const url = apiCivarLogin + token['Access_Token']

            this.api.post('', '', url, false).subscribe({
                next: (data: any) => {
                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve();
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }

    setCredentialsCookie(_u: string, _p: string): void {
        // Cookie.set("__csi_credentials", u + " " + p, 365);
    }

    async hasCredentialsCookie(): Promise<boolean> {
        // const credentials = await get('credentials');
        const credentials = JSON.parse((await SecureStorage.get('account_credentials') as string) ?? '{}') as UserCredentials;


        if (!this.utilityServ.isEmptyObject(credentials)) {
            // console.log('hasCredentialsCookie: ', true);
            return true;
        } else {
            // console.log('hasCredentialsCookie: ', false);
            return false;
        }

        // return document.cookie.indexOf("__csi_credentials") > -1;
    }

    async getCredentialsCookie(): Promise<string[]> {
        // let cookie = Cookie.get("__csi_credentials");
        const credentials = await get<string[]>('credentials');

        if (!this.utilityServ.isEmptyObject(credentials)) {
            return credentials;
        }

        return [];
    }

    /**
     * @name versionSupported
     * @desc Método para mandar plataforma y version de la App y validar si es un version soportada.
     * @param {Object} appInfo viene la info de la version del App
     * @returns {Object} - Promise
     */
    versionSupported(appInfo: appVersionInfo) {
        const records = {
            Version: {
                appVersion: appInfo.appVersion,
                appPlatform: appInfo.appPlatform
            }
        };

        return new Promise((resolve, reject) => {
            const endpoint = apiEndpoints.hasSupport;
            this.api.post(endpoint, records).subscribe({
                next: (data: any) => {
                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve(data);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }


    /**
     * @name validateEmailUser
     * @desc Método para mandar plataforma y version de la App y validar si es un version soportada.
     * @param {Object} appInfo viene la info de la version del App
     * @returns {Object} - Promise
     */
    validateEmailUser(credentials: any) {
        const records = {
            Login: {
                username: credentials.username,
            }
        };

        return new Promise((resolve, reject) => {
            const endpoint = apiEndpoints.userLogin;
            this.api.post(endpoint, records).subscribe({
                next: (data: any) => {
                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve(data);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }

    /**
     * @name goSSOLogin
     * @desc Método para mandar plataforma y version de la App y validar si es un version soportada.
     * @param {Object} appInfo viene la info de la version del App
     * @returns {Object} - Promise
     */
    goSSOLogin(credentials: any) {
        const records = {
            Login: {
                username: credentials.username,
                password: credentials.password,
                // appVersion: versionApp,
                // appPlatform: platformApp
            }
        };

        return new Promise((resolve, reject) => {
            const endpoint = apiEndpoints.SSOLogin;
            this.api.post(endpoint, records).subscribe({
                next: (data: any) => {
                    if (data.ErrorMessage) {
                        reject(data.ErrorMessage);
                    } else {
                        resolve(data);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    reject(error.status + " " + error.statusText);
                },
            })
        });
    }

}
